import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFilm, FaTheaterMasks, FaPencilAlt } from 'react-icons/fa';
import ArtmospherePremiumBackground, { atmospherePremiumPageStyle } from '../ui/backgrounds/ArtmospherePremiumBackground';
import { Home, User } from 'lucide-react';

const thumbnails = [
  {
    icon: <FaPencilAlt className="text-3xl text-amber-500 mb-2" />,
    title: 'Create Scenes',
    desc: 'Build the foundation for any story—screenplays, movies, books, and theater—by designing your own scenes!'
  },
  {
    icon: <FaFilm className="text-3xl text-amber-500 mb-2" />,
    title: 'Bring Stories to Life',
    desc: 'Turn your ideas into real visuals. Artmosphere helps you see your imagination on the screen!'
  },
  {
    icon: <FaTheaterMasks className="text-3xl text-amber-500 mb-2" />,
    title: 'Consistent Characters',
    desc: 'Keep your characters looking the same in every scene—just like the pros do in movies, books, and plays.'
  },
];

export default function StoryboardingLanding() {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen overflow-hidden" style={atmospherePremiumPageStyle}>
      <ArtmospherePremiumBackground variant="aurora" intensity={0.38} />

      <div className="relative z-10 p-1.5 md:p-6">
        <div className="max-w-7xl mx-auto bg-gradient-to-br from-white/92 via-amber-50/80 to-orange-50/80 backdrop-blur-md border border-amber-200/80 rounded-2xl md:rounded-3xl shadow-[0_24px_70px_-24px_rgba(120,53,15,0.45)] p-2 md:p-6">
        {/* Navigation buttons */}
        <div className="flex justify-between items-center mb-4 md:mb-6 gap-1 md:gap-3">
          <div />
          <div className="flex items-center gap-0.5 md:gap-3">
            <button
              onClick={() => navigate('/my-art-world')}
              className="p-1 md:px-4 md:py-2 bg-white/80 backdrop-blur-sm border border-amber-300/50 text-amber-700 font-medium rounded-full shadow-sm hover:bg-amber-100 transition flex items-center justify-center"
              title="Artist Corner"
            >
              <Home className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0" />
              <span className="hidden md:inline ml-1 text-[7px] md:text-sm">Artist Corner</span>
            </button>
            <button
              onClick={() => navigate('/home')}
              className="p-1 md:px-4 md:py-2 bg-white/80 backdrop-blur-sm border border-amber-300/50 text-amber-700 font-medium rounded-full shadow-sm hover:bg-amber-100 transition flex items-center justify-center"
              title="Home"
            >
              <Home className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0" />
              <span className="hidden md:inline ml-1 text-[7px] md:text-sm">Home</span>
            </button>
            <button
              onClick={() => navigate('/artist-wall')}
              className="p-1 md:px-4 md:py-2 bg-white/80 backdrop-blur-sm border border-amber-300/50 text-amber-700 font-medium rounded-full shadow-sm hover:bg-amber-100 transition flex items-center justify-center"
              title="My Gallery"
            >
              <User className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0" />
              <span className="hidden md:inline ml-1 text-[7px] md:text-sm">My Gallery</span>
            </button>
          </div>
        </div>
        
        {/* Welcome Message - Full Width */}
        <div className="w-full text-center py-5 md:py-7 mt-1 md:mt-2 rounded-xl md:rounded-2xl border border-amber-200/80 bg-white/70">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3 md:mb-4">
            Welcome to Storyboarding Studio!
          </h1>
          <p className="text-lg md:text-xl text-slate-700 mb-4 md:mb-6 max-w-4xl mx-auto px-4 md:px-6">
            Dream big! Here you can create scenes—the foundation for every great story, movie, book, or play. Plan your own scenes, create characters, and let AI bring your ideas to life!
          </p>
        </div>

        {/* Main Content */}
        <div className="w-full max-w-6xl mx-auto flex flex-col md:flex-row items-start justify-center p-2 md:p-6 gap-4 md:gap-8 mt-3 md:mt-5">
        {/* Left: Large Video Placeholder */}
        <div className="flex-1 flex flex-col items-center justify-center w-full md:min-w-[400px] max-w-xl">
          <div className="w-full aspect-[16/9] bg-amber-100 border-2 md:border-4 border-amber-300 rounded-xl md:rounded-2xl flex items-center justify-center shadow-xl md:shadow-2xl mb-2 md:mb-4 min-h-[200px] md:min-h-[320px] max-h-[250px] md:max-h-[420px]">
            <span className="text-4xl md:text-7xl text-amber-400 animate-pulse">🎬</span>
          </div>
          <div className="text-center text-slate-600 font-semibold mt-1 md:mt-2 text-xs md:text-base px-2 break-words">(Video coming soon: How to use Storyboarding!)</div>
        </div>
        
        {/* Right: 3 Horizontal Thumbnails + What is Storyboarding + Start Button */}
        <div className="flex-1 flex flex-col items-center justify-center w-full md:min-w-[500px] max-w-2xl h-full">
          {/* What is Storyboarding Section */}
          <div className="w-full mb-3 md:mb-4">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-3 md:p-4 shadow-lg border-2 border-amber-300">
              <h3 className="font-bold text-slate-900 text-base md:text-lg mb-2 text-center">What is Storyboarding?</h3>
              <p className="text-slate-700 text-xs md:text-sm leading-relaxed">
                Storyboarding is planning your story visually, scene by scene. It's how filmmakers, authors, and playwrights organize their ideas. Think of it as drawing a comic book of your story!
              </p>
            </div>
          </div>

          {/* 3 Horizontal Thumbnails */}
          <div className="w-full mb-3 md:mb-4">
            <div className="grid grid-cols-3 gap-2 md:gap-4">
              {thumbnails.map((t, i) => (
                <div key={i} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-2 md:p-4 shadow-lg border-2 border-amber-300 flex flex-col items-center text-center hover:from-amber-100 hover:to-orange-100 transition-all duration-300">
                  {t.icon}
                  <div className="font-bold text-slate-900 text-xs md:text-base mb-1 md:mb-2">{t.title}</div>
                  <div className="text-slate-700 text-[10px] md:text-sm leading-relaxed">{t.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Start Button */}
          <button
            onClick={() => navigate('/storyboarding-studio')}
            className="bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500 text-white font-bold py-2 md:py-3 px-4 md:px-8 rounded-2xl shadow-xl text-sm md:text-xl transition-all duration-300 transform hover:scale-105 border-2 border-amber-300"
          >
            Start Storyboarding!
          </button>
        </div>
        </div>
        </div>
    </div>
    </div>
  );
} 